import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import PkiVoiceRecorder from "@/components/pflege-ki/PkiVoiceRecorder";
import PkiFreigabeModal from "@/components/pflege-ki/PkiFreigabeModal";
import PkiHalluzinationsAmpel from "@/components/pflege-ki/PkiHalluzinationsAmpel";
import PkiCrossDocCheck from "@/components/pflege-ki/PkiCrossDocCheck";
import PkiHalluzWertungBanner from "@/components/pflege-ki/PkiHalluzWertungBanner";
import PkiBeobachtungBanner from "@/components/pflege-ki/PkiBeobachtungBanner";
import PkiQprBox from "@/components/pflege-ki/PkiQprBox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Extrahiert ein Banner-Daten-Objekt für eine bestimmte Regel aus dem
 * `halluzinationscheck.findings`-Array. Liefert null, wenn keine Treffer.
 */
function extractRegelBlock(halluz, regelName) {
  const findings = (halluz?.findings || []).filter((f) => f.regel === regelName);
  if (findings.length === 0) return null;
  // Schweregrad ableiten: rot, wenn irgendein Finding raw_severity="rot"
  const sev = findings.some((f) => (f.raw_severity || "rot") === "rot") ? "rot" : "gelb";
  const label = regelName === "wertung_ohne_datenbasis"
    ? "Wertungen ohne Datenbasis erkannt – bitte fachlich prüfen"
    : "Beobachtungen wurden zu Diagnosen hochgestuft – bitte fachlich prüfen";
  return { regel: regelName, findings, count: findings.length, severity: sev, label };
}

const CTA_LABELS = {
  sis: "SIS-Entwurf erstellen",
  massnahmen: "Maßnahmenplan erstellen",
  pflegebericht: "Pflegebericht erstellen",
  kassenbrief: "Kassenbrief erstellen",
  hoehergruppe: "Höherstufung erstellen",
  qualitaet: "Qualitätscheck starten",
};

const HINTS = {
  sis: 'Erzeugt eine strukturierte SIS (A1–A6) aus den Feldern unter „Eingabe".',
  massnahmen: "Erzeugt einen Maßnahmenplan in 8-Spalten-Tabelle (Problem · Ziel · Maßnahme · Häufigkeit · Zeitraum / Gültigkeit · Verantwortung · Beobachtung · Evaluation) mit Kopfblock (Erstellungsdatum, Gültig ab, Nächste Evaluation, Status).",
  pflegebericht: "Sachlicher Verlaufseintrag — nur Beobachtungen & Maßnahmen, keine Rückfragen. Wähle Fließtext oder 9-Bereich-Tabelle.",
  kassenbrief: "Pflegekasse (SGB XI), Krankenkasse (SGB V) oder Höherstufungs-Begründung — Mehrbedarf statt Diagnosen, Folgen-bei-Nichtbewilligung sind Teil des Briefs.",
  hoehergruppe: "Erstellt einen Höherstufungs- oder Widerspruchs-Entwurf mit klarem, nicht-übertriebenen Hilfebedarf.",
  qualitaet: "Lässt die KI auf SIS, Maßnahmenplan, Pflegebericht & Kassenbrief schauen und prüft Konsistenz, Lücken, Evaluationen, fehlende BZ-Werte/Insulindosis/Verordnung.",
};


/**
 * Result-Tab pro KI-Funktion · zeigt Output, CTAs, Status-Workflow.
 */
export default function PkiResultTab({
  tabId, kind, label, output, onGenerate, onSave, busy, customerLabel,
  hasInput, rohbericht, setRohbericht, sisText, planText,
}) {
  const [editText, setEditText] = useState(output?.text || "");
  const [extraContext, setExtraContext] = useState("");
  const editAreaRef = useRef(null);
  // Variant-Steuerung: Pflegebericht=fliesstext|tabelle, Kassenbrief=pflegekasse|krankenkasse|hoeherstufung
  const [variant, setVariant] = useState(
    kind === "pflegebericht" ? "fliesstext" :
    kind === "kassenbrief"   ? "pflegekasse" : null
  );

  // Datum/Uhrzeit des Eintrags · Default = jetzt (lokale Zeit für datetime-local).
  const nowLocal = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16); // 'YYYY-MM-DDTHH:MM'
  };
  const [entryDate, setEntryDate] = useState(nowLocal());

  const fmtDateDe = (iso) => {
    if (!iso) return "[DATUM/UHRZEIT]";
    try {
      const d = new Date(iso);
      return d.toLocaleString("de-DE", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      }) + " Uhr";
    } catch { return iso; }
  };

  // Replace placeholder in LLM output with actual entry date
  useEffect(() => {
    const incoming = output?.text || "";
    const replaced = incoming.replace(/\[DATUM\s*\/\s*UHRZEIT\]/gi, fmtDateDe(entryDate));
    setEditText(replaced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [output?.text]);

  // Wenn der User das Datum nachträglich ändert, ersetzen wir den Platzhalter
  // erneut – aber nur wenn er noch im Text steht.
  useEffect(() => {
    setEditText((prev) => prev.replace(/\[DATUM\s*\/\s*UHRZEIT\]/gi, fmtDateDe(entryDate)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryDate]);

  const exportPdf = async () => {
    let draftId = output?.draft_id;
    // Auto-Save wenn noch keine ID vorhanden
    if (!draftId) {
      const saved = await onSave(kind, editText, "entwurf");
      draftId = saved?.id;
      if (!draftId) {
        toast.error("Konnte Entwurf nicht speichern – PDF nicht erstellt.");
        return;
      }
    }
    try {
      const r = await axios.get(`${API}/pflege-ki/drafts/${draftId}/pdf`, {
        withCredentials: true, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Pflege-KI-${kind}-${draftId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen.");
    } catch (e) {
      toast.error("PDF-Export fehlgeschlagen.");
    }
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(editText);
      toast.success("Text in die Zwischenablage kopiert.");
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  const handleSave = async (status = "entwurf") => {
    return await onSave(kind, editText, status);
  };

  // Mail-Versand-Dialog State
  const [mailOpen, setMailOpen] = useState(false);
  const [mailTo, setMailTo] = useState("");
  const [mailSubject, setMailSubject] = useState("");
  const [mailNote, setMailNote] = useState("");
  const [mailSending, setMailSending] = useState(false);

  // Freigabe-Modal (Haftungsbestätigung) State
  const [freigabeOpen, setFreigabeOpen] = useState(false);
  const [wasEdited, setWasEdited] = useState(false);

  // Editierung tracken — sobald der User den Text gegenüber dem LLM-Output ändert.
  useEffect(() => {
    const original = output?.text || "";
    if (original && editText && editText !== original) {
      const cleanedOriginal = original.replace(/\[DATUM\s*\/\s*UHRZEIT\]/gi, "").trim();
      const cleanedEdit = editText.replace(/\[DATUM\s*\/\s*UHRZEIT\]/gi, "").trim();
      if (cleanedOriginal && cleanedEdit && cleanedOriginal !== cleanedEdit) {
        setWasEdited(true);
      }
    }
  }, [editText, output?.text]);

  const openFreigabeModal = async () => {
    let draftId = output?.draft_id;
    if (!draftId) {
      const saved = await onSave(kind, editText, "entwurf");
      draftId = saved?.id;
      if (!draftId) {
        toast.error("Konnte Entwurf nicht speichern – Freigabe abgebrochen.");
        return;
      }
    }
    setFreigabeOpen(true);
  };

  const handleFreigabeConfirmed = (freigabe) => {
    // onSave nicht erneut callen — Backend hat den Draft schon auf 'freigegeben' gesetzt.
    if (freigabe?.draft_id && output?.draft_id) {
      // Output-State markieren · keine Re-Render-Lücke
      // (Status-Update wird durch nächstes Reload sichtbar.)
    }
  };

  const openMailDialog = async () => {
    // Sicherstellen, dass der Entwurf gespeichert ist (als Entwurf — Freigabe läuft separat via Modal)
    if (!output?.draft_id) {
      const saved = await onSave(kind, editText, "entwurf");
      if (!saved?.id) {
        toast.error("Bitte zuerst Entwurf speichern.");
        return;
      }
    }
    setMailSubject(`${label} · ${customerLabel || "Pflege-Dokument"}`);
    setMailOpen(true);
  };

  const sendMail = async () => {
    if (!mailTo || !mailTo.includes("@")) {
      toast.error("Bitte gültige Empfänger-E-Mail eingeben.");
      return;
    }
    let draftId = output?.draft_id;
    if (!draftId) {
      const saved = await onSave(kind, editText, "entwurf");
      draftId = saved?.id;
    }
    if (!draftId) {
      toast.error("Entwurf konnte nicht gespeichert werden.");
      return;
    }
    setMailSending(true);
    try {
      await axios.post(`${API}/pflege-ki/drafts/${draftId}/send-mail`, {
        to_email: mailTo.trim(),
        subject: mailSubject.trim(),
        note: mailNote.trim() || null,
      }, { withCredentials: true });
      toast.success(`Mail an ${mailTo} versendet (mit PDF im Anhang).`);
      setMailOpen(false);
      setMailNote("");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Mail-Versand fehlgeschlagen.";
      // Backend lehnt Mail-Versand für nicht-freigegebene Drafts ab → klare User-Anleitung
      if (typeof msg === "string" && msg.toLowerCase().includes("freigegeben")) {
        toast.error('Bitte zuerst den Entwurf via "Freigeben" mit Haftungsmodal freigeben.');
      } else {
        toast.error(msg);
      }
    } finally {
      setMailSending(false);
    }
  };

  // Generate-Kontext: Datum + (für Qualitätscheck) eingegebener Prüftext
  const buildExtraContext = () => {
    const dateLine = `Bezugsdatum/-uhrzeit dieses Eintrags: ${fmtDateDe(entryDate)}. Verwende dieses Datum überall dort, wo „[DATUM/UHRZEIT]" stehen würde.`;
    if (kind === "qualitaet" && extraContext.trim()) {
      return `${dateLine}\n\n${extraContext}`;
    }
    return dateLine;
  };

  return (
    <div className="space-y-5" data-testid={`pki-result-${tabId}`}>
      <div className="rounded-2xl border border-cream-300 bg-cream-50 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
              Pflege-KI · {label}
            </div>
            <div className="mt-1 text-[13px] text-sand-600 leading-snug">
              {HINTS[kind]}
            </div>
            {customerLabel && (
              <div className="mt-2 text-[12px] text-sand-500">
                Bezug: <span className="font-semibold text-sand-800">{customerLabel}</span>
              </div>
            )}
            <div className="mt-3">
              <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1">
                Datum / Uhrzeit (für Entwurf, PDF & Bericht)
              </label>
              <input
                type="datetime-local"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                data-testid={`pki-entry-date-${kind}`}
                className="h-10 rounded-xl border border-cream-300 bg-white px-3 text-[13px] text-sand-800 focus:outline-none focus:border-terra"
              />
              <span className="ml-2 text-[12px] text-sand-500">→ {fmtDateDe(entryDate)}</span>
            </div>
          </div>
          <Button
            onClick={() => onGenerate(kind, buildExtraContext(), variant)}
            disabled={busy || (!hasInput && kind !== "pflegebericht" && kind !== "qualitaet")}
            data-testid={`pki-generate-${kind}`}
            className="rounded-full h-11 px-6 bg-terra hover:bg-terra-dark text-white font-semibold">
            {busy ? "Erzeuge Entwurf …" : (CTA_LABELS[kind] || "Erzeugen")}
          </Button>
        </div>

        {/* Variant-Toggle · Pflegebericht: Fließtext vs Tabelle */}
        {kind === "pflegebericht" && (
          <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="pki-pb-variant">
            <span className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500">
              Ausgabeformat
            </span>
            {[
              { id: "fliesstext", label: "Fließtext (kompakt)" },
              { id: "tabelle",    label: "Tabelle (9 Bereiche)" },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setVariant(opt.id)}
                data-testid={`pki-pb-variant-${opt.id}`}
                className={`text-[12px] px-3 h-7 rounded-full border ${
                  variant === opt.id
                    ? "bg-sand-800 text-cream-50 border-sand-800"
                    : "bg-white text-sand-700 border-cream-300 hover:border-sand-400"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* Variant-Toggle · Kassenbrief: 3 Modi */}
        {kind === "kassenbrief" && (
          <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="pki-kb-variant">
            <span className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500">
              Brief-Modus
            </span>
            {[
              { id: "pflegekasse",  label: "Pflegekasse (SGB XI)" },
              { id: "krankenkasse", label: "Krankenkasse (SGB V)" },
              { id: "hoeherstufung", label: "Höherstufungs-Begründung" },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setVariant(opt.id)}
                data-testid={`pki-kb-variant-${opt.id}`}
                className={`text-[12px] px-3 h-7 rounded-full border ${
                  variant === opt.id
                    ? "bg-sand-800 text-cream-50 border-sand-800"
                    : "bg-white text-sand-700 border-cream-300 hover:border-sand-400"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* Spezial: Pflegebericht braucht Rohbericht-Stichpunkte */}
        {kind === "pflegebericht" && (
          <div className="mt-4 space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500">
                Stichpunkte / Diktat (kurz)
              </label>
              <PkiVoiceRecorder
                onTranscript={(text) => {
                  // an bestehenden Text anhängen, mit Leerzeichen falls nötig
                  const sep = (rohbericht && !rohbericht.endsWith(" ") && !rohbericht.endsWith("\n")) ? " " : "";
                  setRohbericht((rohbericht || "") + sep + text);
                }}
                disabled={busy}
              />
            </div>
            <textarea
              rows={4}
              value={rohbericht}
              onChange={(e) => setRohbericht(e.target.value)}
              placeholder='z. B. „Kunde heute müde, BZ 178, Medikamente genommen, keine Schmerzen." · oder Mikrofon-Button nutzen.'
              data-testid="pki-rohbericht"
              className="w-full rounded-xl border border-cream-300 bg-white px-3 py-2 text-[13.5px] text-sand-800 focus:outline-none focus:border-terra"
            />
            <p className="text-[11.5px] text-sand-500">
              Tipp: Auf Mobilger&auml;ten Mikrofon nutzen &ndash; sprich 10&ndash;60 Sek. Stichpunkte, Whisper macht den Text. Danach &bdquo;Pflegebericht erstellen&ldquo; &rarr; KI erstellt sachlichen Bericht.
            </p>
          </div>
        )}

        {/* Spezial: Qualitätscheck darf zusätzlichen Kontext (z. B. fertige SIS) bekommen */}
        {kind === "qualitaet" && (
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1.5">
                Zu prüfender Text (optional · z. B. fertige SIS, Maßnahmenplan oder Kassenbrief)
              </label>
              <textarea
                rows={5}
                value={extraContext}
                onChange={(e) => setExtraContext(e.target.value)}
                placeholder="Füge hier SIS- und/oder Maßnahmenplan-Text ein, den die KI auf Konsistenz prüfen soll."
                data-testid="pki-quality-context"
                className="w-full rounded-xl border border-cream-300 bg-white px-3 py-2 text-[13px] text-sand-800 focus:outline-none focus:border-terra font-mono"
              />
            </div>
            <KbDeterministicCheck contextText={extraContext} />
          </div>
        )}
      </div>

      {/* OUTPUT */}
      {output?.text ? (
        <div className="rounded-2xl border border-cream-300 bg-white p-5"
          data-testid={`pki-output-${tabId}`}>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-clay-dark">
              KI-Entwurf · editierbar
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={copyText} variant="outline" size="sm"
                data-testid={`pki-copy-${kind}`}
                className="h-9 rounded-full border-clay">
                Text kopieren
              </Button>
              <Button onClick={() => handleSave("entwurf")} variant="outline" size="sm"
                data-testid={`pki-save-${kind}`}
                className="h-9 rounded-full border-clay">
                Entwurf speichern
              </Button>
              <Button onClick={() => handleSave("pruefung")} variant="outline" size="sm"
                data-testid={`pki-pruefen-${kind}`}
                className="h-9 rounded-full border-terra text-terra">
                In Prüfung
              </Button>
              <Button onClick={openFreigabeModal} size="sm"
                data-testid={`pki-freigeben-${kind}`}
                className="h-9 rounded-full bg-sage-dark hover:bg-sage-dark/90 text-white">
                Freigeben
              </Button>
              <Button onClick={exportPdf} size="sm"
                data-testid={`pki-pdf-${kind}`}
                className="h-9 rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50">
                PDF
              </Button>
              <Button onClick={openMailDialog} size="sm"
                data-testid={`pki-mail-${kind}`}
                className="h-9 rounded-full bg-brand-darkred hover:bg-brand-darkred/90 text-white">
                Per Mail senden
              </Button>
            </div>
          </div>
          {/* Halluzinationscheck-Ampel (nur Maßnahmenplan) */}
          {kind === "massnahmen" && output?.halluzinationscheck && (
            <PkiHalluzinationsAmpel
              halluz={output.halluzinationscheck}
              onJumpTo={(regel) => {
                const ta = editAreaRef.current;
                if (!ta) return;
                const map = {
                  "Transfer-Selbstständigkeit": ["transfer"],
                  "Toilettentraining ohne Indikation": ["toilettentraining", "toilettengang"],
                  "Medikamenten-Verantwortlichkeit": ["medikament", "stell"],
                  "Freiheitsentziehende Maßnahme": ["türsicher", "tuersicher", "bettgitter", "gurt", "sensormatte"],
                  "Häufigkeit ohne Quelle": ["gewicht"],
                  "Trinkmenge ohne SIS-Beleg": ["trinkmenge", "flüssigkeits"],
                  "Blutdruck-Schwellwert ohne SIS-Beleg": ["rr", "blutdruck"],
                  "Wechselintervall ohne SIS-Beleg": ["wechsel", "lagerung"],
                };
                const needles = map[regel] || [regel.toLowerCase()];
                const text = ta.value || "";
                const low = text.toLowerCase();
                let idx = -1;
                for (const n of needles) {
                  idx = low.indexOf(n);
                  if (idx >= 0) break;
                }
                if (idx < 0) return;
                ta.focus();
                ta.setSelectionRange(idx, idx + (needles[0]?.length || 1));
                const lineNo = text.slice(0, idx).split("\n").length;
                ta.scrollTop = Math.max(0, (lineNo - 4) * 21);
              }}
            />
          )}

          {/* P0-Halluz-Banner: Wertung ohne Datenbasis + Beobachtung → Diagnose
              · für Briefe (hoehergruppe, kassenbrief, leistungsanpassung) und Maßnahmenpläne */}
          {output?.halluzinationscheck?.findings?.length > 0 && (
            <>
              <PkiHalluzWertungBanner halluz={extractRegelBlock(output.halluzinationscheck, "wertung_ohne_datenbasis")} />
              <PkiBeobachtungBanner data={extractRegelBlock(output.halluzinationscheck, "beobachtung_vs_diagnose")} />
            </>
          )}
          <textarea
            ref={editAreaRef}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={Math.min(40, Math.max(12, editText.split("\n").length + 2))}
            data-testid={`pki-edit-${kind}`}
            className="w-full rounded-xl border border-cream-200 bg-cream-50 px-4 py-3 text-[14px] leading-relaxed text-sand-800 focus:outline-none focus:border-terra font-mono"
          />
          <div className="mt-3 text-[11.5px] text-brand-darkred font-semibold">
            Hinweis: Dieser Text ist ein KI-Entwurf und muss fachlich geprüft und freigegeben werden.
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-cream-300 bg-white p-10 text-center text-sand-500 text-[14px]">
          Noch kein Entwurf erstellt. F&uuml;lle die Eingabemaske und klicke oben auf &bdquo;{CTA_LABELS[kind] || "Erzeugen"}&ldquo;.
        </div>
      )}

      {/* CROSS-DOC-PLAUSIBILITÄTSCHECK · nur im Pflegebericht-Tab */}
      {kind === "pflegebericht" && (
        <PkiCrossDocCheck
          sisText={sisText || ""}
          planText={planText || ""}
          berichtText={editText || ""}
          customerLabel={customerLabel}
        />
      )}

      {/* QPR-2026-READINESS-CHECK · im Pflegebericht- und Maßnahmen-Tab */}
      {(kind === "pflegebericht" || kind === "massnahmen") && (
        <PkiQprBox
          sisText={sisText || ""}
          planText={kind === "massnahmen" ? (editText || "") : (planText || "")}
          berichtText={kind === "pflegebericht" ? (editText || "") : ""}
          customerLabel={customerLabel}
        />
      )}

      {/* MAIL-DIALOG */}
      {mailOpen && (
        <div data-testid={`pki-mail-dialog-${kind}`}
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setMailOpen(false); }}>
          <div className="bg-white rounded-3xl border border-cream-300 max-w-lg w-full p-6 shadow-xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred">
                  Pflege-KI · Mail-Versand
                </div>
                <h3 className="font-display text-xl text-sand-800 mt-1">
                  {label} versenden
                </h3>
              </div>
              <button onClick={() => setMailOpen(false)}
                data-testid={`pki-mail-close-${kind}`}
                className="text-sand-500 hover:text-sand-800 text-xl leading-none">✕</button>
            </div>

            <div className="rounded-xl bg-peach-50 border border-terra/30 p-3 mb-4 text-[12px] text-sand-700 leading-snug">
              <strong className="text-brand-darkred">Wichtig:</strong> Bitte nur freigegebene
              Dokumente versenden. Das PDF inkl. Pflegefachkraft-Freigabe wird automatisch angehängt.
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1">
                  Empfänger-E-Mail
                </label>
                <input
                  type="email"
                  value={mailTo}
                  onChange={(e) => setMailTo(e.target.value)}
                  placeholder="hausarzt@praxis-mueller.de"
                  data-testid={`pki-mail-to-${kind}`}
                  className="w-full h-11 rounded-xl border border-cream-300 bg-cream-50 px-3 text-[14px] text-sand-800 focus:outline-none focus:border-terra"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1">
                  Betreff
                </label>
                <input
                  value={mailSubject}
                  onChange={(e) => setMailSubject(e.target.value)}
                  data-testid={`pki-mail-subject-${kind}`}
                  className="w-full h-11 rounded-xl border border-cream-300 bg-cream-50 px-3 text-[14px] text-sand-800 focus:outline-none focus:border-terra"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1">
                  Persönliche Notiz (optional)
                </label>
                <textarea
                  rows={3}
                  value={mailNote}
                  onChange={(e) => setMailNote(e.target.value)}
                  placeholder="z. B. „Bitte um Rückruf bezüglich Medikation. Vielen Dank."
                  data-testid={`pki-mail-note-${kind}`}
                  className="w-full rounded-xl border border-cream-300 bg-cream-50 px-3 py-2 text-[13.5px] text-sand-800 focus:outline-none focus:border-terra"
                />
              </div>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <Button onClick={() => setMailOpen(false)} variant="outline" size="sm"
                data-testid={`pki-mail-cancel-${kind}`}
                className="h-10 rounded-full border-clay">
                Abbrechen
              </Button>
              <Button onClick={sendMail} disabled={mailSending}
                data-testid={`pki-mail-send-${kind}`}
                className="h-10 rounded-full bg-brand-darkred hover:bg-brand-darkred/90 text-white px-5">
                {mailSending ? "Sende …" : "Senden"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* FREIGABE-MODAL (Haftungsbestätigung) */}
      <PkiFreigabeModal
        open={freigabeOpen}
        onClose={() => setFreigabeOpen(false)}
        draftId={output?.draft_id}
        outputText={editText}
        editedBefore={wasEdited}
        kindLabel={label}
        halluzinationscheck={kind === "massnahmen" ? output?.halluzinationscheck : null}
        onConfirmed={handleFreigabeConfirmed}
      />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
 *  Deterministischer KB-Qualitätscheck · keine LLM-Inferenz
 * ────────────────────────────────────────────────────────────*/
const PRIO_STYLE = {
  hoch:    "bg-brand-darkred text-cream-50",
  mittel:  "bg-saffron text-clay-dark",
  niedrig: "bg-sand-300 text-sand-800",
};

function KbDeterministicCheck({ contextText }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [splitText, setSplitText] = useState({ sis: "", plan: "", brief: "" });
  const [splitOpen, setSplitOpen] = useState(false);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      // 3-Block-Mode wenn manuell aufgesplittet, sonst alles in sis_text
      const payload = splitOpen
        ? {
            sis_text: splitText.sis || "",
            massnahmenplan_text: splitText.plan || "",
            kassenbrief_text: splitText.brief || "",
          }
        : {
            sis_text: contextText || "",
            massnahmenplan_text: contextText || "",
            kassenbrief_text: contextText || "",
          };
      const { data } = await axios.post(`${API}/pflege-ki/kb/qualitaetscheck`, payload, { withCredentials: true });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wissensbibliothek-Check fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-terra/30 bg-peach-50 p-5" data-testid="pki-kb-check">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
            Wissensbibliothek · Deterministischer Check
          </div>
          <div className="mt-1 text-[13px] text-sand-700 leading-snug">
            Prüft den eingegebenen Text gegen alle aktiven Pflege-Regeln (kein LLM-Aufruf, offline-evaluierbar).
            Sortiert nach Priorität.
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setSplitOpen((v) => !v)} variant="outline" size="sm"
            data-testid="pki-kb-split-toggle"
            className="h-9 rounded-full border-clay text-[12.5px]">
            {splitOpen ? "Zurück zu Gesamt-Text" : "Texte aufsplitten (SIS / Plan / Brief)"}
          </Button>
          <Button onClick={run} disabled={busy} size="sm"
            data-testid="pki-kb-check-run"
            className="h-9 rounded-full bg-sage-dark hover:bg-sage-dark/90 text-white">
            {busy ? "Prüfe …" : "KB-Check ausführen"}
          </Button>
        </div>
      </div>

      {splitOpen && (
        <div className="mt-4 grid lg:grid-cols-3 gap-3">
          {[
            { key: "sis", label: "SIS-Text" },
            { key: "plan", label: "Maßnahmenplan" },
            { key: "brief", label: "Kassenbrief" },
          ].map((f) => (
            <div key={f.key}>
              <label className="block text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1">
                {f.label}
              </label>
              <textarea rows={5} value={splitText[f.key]}
                onChange={(e) => setSplitText({ ...splitText, [f.key]: e.target.value })}
                data-testid={`pki-kb-split-${f.key}`}
                className="w-full rounded-xl border border-cream-300 bg-white px-3 py-2 text-[12.5px] focus:outline-none focus:border-terra font-mono leading-snug" />
            </div>
          ))}
        </div>
      )}

      {result && (
        <div className="mt-5" data-testid="pki-kb-check-results">
          {result.count === 0 ? (
            <div className="rounded-xl bg-sage-dark/10 border border-sage-dark/30 p-4 text-[13px] text-sand-800">
              ✓ Keine Befunde — der Text erfüllt die geprüften Regeln.
            </div>
          ) : (
            <>
              <div className="text-[11.5px] uppercase tracking-[0.22em] font-bold text-clay-dark mb-3">
                {result.count} Befund(e) gefunden · sortiert nach Priorität
              </div>
              <ul className="space-y-2.5">
                {result.findings.map((f, i) => (
                  <li key={i} className="rounded-2xl border border-cream-300 bg-white p-4"
                    data-testid={`pki-kb-finding-${i}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex h-6 px-2.5 items-center rounded-full text-[10.5px] uppercase tracking-[0.18em] font-bold ${PRIO_STYLE[f.prioritaet] || ""}`}>
                          {f.prioritaet}
                        </span>
                        <span className="text-[12.5px] font-bold text-sand-800">{f.kategorie}</span>
                        <span className="text-[11.5px] text-sand-500">· {f.fundstelle}</span>
                      </div>
                    </div>
                    <div className="text-[13px] text-sand-800 font-semibold leading-snug">{f.problem}</div>
                    <div className="mt-1.5 text-[12.5px] text-sand-600 leading-relaxed">
                      <strong className="text-sand-700">Warum wichtig:</strong> {f.warum_wichtig}
                    </div>
                    <div className="mt-1 text-[12.5px] text-sage-dark leading-relaxed">
                      <strong>Vorschlag:</strong> {f.vorschlag}
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-[11.5px] text-sand-500 italic">
                {result.disclaimer}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
